

import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaStickyNote, FaTag, FaStar, FaUserSecret, FaHandshake, FaUsers, FaUserClock } from "react-icons/fa";
import api, { BACKEND_URL } from '../api/axios';
import Navbar from "./Navbar";
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';
import CandidateNotesModal from "./CandidateNotesModal";
import ScheduleInterviewModal from "./ScheduleInterviewModal";
import CreateOfferModal from "./CreateOfferModal";


const COLORS = { blue: "#7C3AED", orange: "#F59E0B", dark: "#0D1B3D" };

const GRADIENT_BUTTON =
  "bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] text-white font-semibold rounded-[18px] shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/35 hover:scale-[1.02] active:scale-[0.99] transition-all duration-200";

const GRADIENT_TEXT =
  "bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent";

const STAGES = [
  { key: "applied", label: "Applied" },
  { key: "screening", label: "Screening" },
  { key: "shortlisted", label: "Shortlisted" },
  { key: "interview_round_1", label: "Interview Round 1" },
  { key: "interview_round_2", label: "Interview Round 2" },
  { key: "hr_round", label: "HR Round" },
  { key: "offer", label: "Offer" },
  { key: "joined", label: "Joined" },
  { key: "on_hold_withdrawn", label: "On Hold / Withdrawn" },
  { key: "rejected", label: "Rejected" },
  { key: "other", label: "Other" },
];

// Maps every real job_applications.status value onto a board column.
// viewed -> applied, contacted -> shortlisted, selected/offer_accepted -> offer,
// completed -> joined, on_hold/withdrawn -> on_hold_withdrawn.
// Anything not listed here falls into "other" instead of vanishing.
const STATUS_TO_COLUMN = {
  applied: "applied",
  viewed: "applied",
  screening: "screening",
  shortlisted: "shortlisted",
  contacted: "shortlisted",
  interview_round_1: "interview_round_1",
  interview_round_2: "interview_round_2",
  hr_round: "hr_round",
  offer: "offer",
  selected: "offer",
  offer_accepted: "offer",
  joined: "joined",
  completed: "joined",
  on_hold: "on_hold_withdrawn",
  withdrawn: "on_hold_withdrawn",
  rejected: "rejected",
};

// Only statuses folded into a wider column need a badge on the card;
// a status that already owns its own column (applied, screening, etc.)
// is redundant with the column header, so no entry here means no badge.
const STATUS_LABELS = {
  viewed: "Viewed",
  contacted: "Contacted",
  selected: "Selected",
  offer_accepted: "Offer Accepted",
  completed: "Completed",
  on_hold: "On Hold",
  withdrawn: "Withdrawn",
};

const DIRECT_STATUS_OPTIONS = STAGES.filter(
  (s) => !["joined", "on_hold_withdrawn", "other"].includes(s.key)
);

function StatusDropdown({ value, label, options, onChange, disabled, bgColor, textColor }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (!open && wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUp(spaceBelow < 240 && spaceAbove > spaceBelow);
    }
    setOpen((o) => !o);
  };

  return (
    <div className="relative inline-block" ref={wrapRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={toggleOpen}
        disabled={disabled}
        className="mb-2 block rounded-full px-2 py-0.5 text-xs font-semibold border-0 text-left"
        style={{ backgroundColor: bgColor, color: textColor }}
      >
        {label} <span className="ml-1">▾</span>
      </button>
      {open && (
        <div
          className={`absolute z-20 max-h-56 w-44 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg ${
            openUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          {options.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                setOpen(false);
                onChange(s.key);
              }}
              className="block w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100"
              style={{ color: COLORS.dark }}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function labelForStatus(status) {
  return STAGES.find((s) => s.key === status)?.label || STATUS_LABELS[status] || status;
}

// job_applications.status = 'joined' can only be reached through the
// joining-confirmation flow (POST /joinings -> PUT /joinings/{id}/mark-joined).
// PipelineController::bulkMove() rejects status=joined outright (422) — see
// its docblock. Dropping a card on this column must never call bulk-move.
const JOINED_STAGE_KEY = "joined";

// Small modal collecting the joining date (and optional salary) for a single
// application, then running the two-step confirm flow against /joinings.
function JoiningConfirmModal({ application, onClose, onConfirmed }) {
  const today = new Date().toISOString().slice(0, 10);
  const [joiningDate, setJoiningDate] = useState(today);
  const [salary, setSalary] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    if (!joiningDate) {
      setError("Joining date is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data: joining } = await api.post("/joinings", {
        job_application_id: application.id,
        joining_date: joiningDate,
        salary: salary ? Number(salary) : undefined,
      });
      await api.put(`/joinings/${joining.id}/mark-joined`);
      onConfirmed();
    } catch (err) {
      const msg = err?.response?.data?.message
        || Object.values(err?.response?.data?.errors || {})[0]?.[0]
        || "Couldn't confirm this joining.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between rounded-t-xl px-6 py-4" style={{ backgroundColor: COLORS.dark }}>
          <h2 className="text-sm font-semibold text-white">
            Confirm joining — {application.candidate_name}
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white" disabled={submitting}>
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Joining date</label>
            <input
              type="date"
              value={joiningDate}
              onChange={(e) => setJoiningDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Salary (optional, ₹/year)</label>
            <input
              type="number"
              min="0"
              value={salary}
              onChange={(e) => setSalary(e.target.value)}
              placeholder="e.g. 600000"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <p className="text-xs text-gray-400">
            This creates the joining record and marks it confirmed immediately, which also
            creates the placement and its retention checkpoints (7/30/60/90 days).
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitting}
              className="rounded-md px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
              style={{ backgroundColor: COLORS.blue }}
            >
              {submitting ? "Confirming…" : "Confirm joining"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
function ApplicantDetailModal({ application, onClose, onUpdate, onOffer, onSchedule }) {
  const app = application;
  const stageLabel = STAGES.find((s) => s.key === app.status)?.label || app.status;

  // Only these are safe to set directly from this dropdown -- interview
  // stages need round type + date/time (Schedule Interview flow) and
  // 'joined' needs to create Joining/Placement/Retention records (Confirm
  // joining flow), so both stay display-only here, same gating as
  // bulk-move and the single-application status endpoint.
  const DIRECT_STATUS_OPTIONS = STAGES.filter(
    (s) => !["joined", "on_hold_withdrawn", "other"].includes(s.key)
  );

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusError, setStatusError] = useState("");

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || newStatus === app.status) return;

    // Same as the board: "Offer" needs the Create Offer form (salary +
    // joining date) to actually create the offer, generate the PDF, and
    // send the email -- not a plain status flip.
    if (newStatus === "offer") {
      if (onOffer) onOffer(app);
      onClose();
      return;
    }

    // Interview/HR stages need round type + date/time to send
    // InterviewScheduledMail -- route through the real Schedule Interview
    // flow instead of a plain status PUT.
    if (["interview_round_1", "interview_round_2", "hr_round"].includes(newStatus)) {
      if (onSchedule) onSchedule(app);
      onClose();
      return;
    }

    setUpdatingStatus(true);
    setStatusError("");
    try {
      await api.put(`/applications/${app.id}/status`, { status: newStatus });
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      setStatusError(err?.response?.data?.message || "Couldn't update status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl max-h-[85vh] overflow-y-auto">
        <div
          className="flex items-center justify-between rounded-t-xl px-6 py-4 sticky top-0"
          style={{ backgroundColor: COLORS.dark }}
        >
          <h2 className="text-sm font-semibold text-white">Applicant Details</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white text-lg leading-none">
            ×
          </button>
        </div>

        <div className="p-5 space-y-5">
        {/* Candidate section */}
          <div>
            <p className="text-xs font-semibold uppercase text-gray-400 mb-2">Candidate</p>
            <p className="text-base font-semibold" style={{ color: COLORS.dark }}>
              {app.candidate_name}
            </p>

            <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
              {app.experience_years != null && <span>{app.experience_years} yrs experience</span>}
              {app.expected_salary != null && (
                <span>Expects ₹{Number(app.expected_salary).toLocaleString("en-IN")}</span>
              )}
              {app.email && <span>{app.email}</span>}
            </div>

            {app.phone && (
              <a
                href={`tel:${app.phone}`}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium"
                style={{ color: COLORS.blue }}
              >
                📞 {app.phone}
              </a>
            )}

            {app.tags && app.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {app.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                    style={{ backgroundColor: "#FFFBEB", color: COLORS.orange }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Job section */}
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold uppercase text-gray-400 mb-2">Job</p>
            <p className="text-sm font-medium" style={{ color: COLORS.dark }}>
              {app.job_title}
            </p>
            <StatusDropdown
              value={app.status}
              label={stageLabel}
              options={DIRECT_STATUS_OPTIONS}
              onChange={handleStatusChange}
              disabled={updatingStatus}
              bgColor="#F5F3FF"
              textColor={COLORS.blue}
            />
            {statusError && <p className="mt-1 text-xs text-red-500">{statusError}</p>}
          </div>

          {/* Notes section */}
          {app.notes && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold uppercase text-gray-400 mb-2">Notes</p>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{app.notes}</p>
            </div>
          )}

         {/* Resume, if the application row carries one */}
          {app.resume_path && (
            <div className="border-t border-gray-100 pt-4">
              <a
                href={app.resume_path}
                target="_blank"
                rel="noreferrer"
                className="inline-block rounded-md px-4 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: COLORS.blue }}
              >
                📄 View Resume
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EmployerPipeline() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [applications, setApplications] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [jobFilter, setJobFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [jobsError, setJobsError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [dragOverStage, setDragOverStage] = useState(null);
  const [notesTarget, setNotesTarget] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

// Candidate currently going through the "confirm joining" modal.
  const [joiningTarget, setJoiningTarget] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [offerTarget, setOfferTarget] = useState(null);

  // Candidate whose full details are shown in the view-details popup.
  const [detailTarget, setDetailTarget] = useState(null);

  const [matchScores, setMatchScores] = useState({});
  const [scoring, setScoring] = useState(false);
  const [scoreError, setScoreError] = useState("");

  // AI Recommendations
  const [recommendations, setRecommendations] = useState(null);
  const [loadingReco, setLoadingReco] = useState(false);
  const [recoError, setRecoError] = useState("");
  const [similarTarget, setSimilarTarget] = useState(null);
  const [similarResults, setSimilarResults] = useState(null);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  // Mobile-only: sidebar drawer toggle (same pattern as other employer pages)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const loadApplications = () => {
    setLoading(true);
    setError("");
    api
      .get("/pipeline", jobFilter ? { params: { job_posting_id: jobFilter } } : undefined)
      .then((res) => setApplications(res.data))
      .catch(() => setError("Couldn't load the pipeline."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    setMatchScores({});
    setRecommendations(null);
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobFilter]);

  useEffect(() => {
    api
      .get("/jobs")
      .then((res) => setJobPostings(res.data))
      .catch(() => setJobsError("Couldn't load job list."));
  }, []);

  const columns = useMemo(() => {
    const grouped = {};
    STAGES.forEach((s) => (grouped[s.key] = []));
    applications.forEach((a) => {
      const columnKey = STATUS_TO_COLUMN[a.status] || "other";
      grouped[columnKey].push(a);
    });
    return grouped;
  }, [applications]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleDrop = async (stage) => {
    setDragOverStage(null);
    const ids = Array.from(selected);
    if (ids.length === 0 || bulkActionLoading) return;

    // These are display-only groupings (STATUS_TO_COLUMN folds several real
    // statuses into one column) -- not real job_applications.status values,
    // so there is nothing valid to bulk-move to. Withdrawn only happens when
    // the candidate withdraws themselves; on_hold isn't set from this board.
    if (stage === "on_hold_withdrawn" || stage === "other") {
      setError("Can't drop directly into this column — it groups several statuses together.");
      return;
    }

    // 'joined' can't go through bulk-move — the API rejects it (422) by design,
    // since that transition has to create Joining/Placement/Retention records,
    // not just flip a status column. Route it through the real confirmation
    // flow instead, one candidate at a time.
    if (stage === JOINED_STAGE_KEY) {
      if (ids.length > 1) {
        setError("Select only one candidate at a time to confirm a joining.");
        return;
      }
      const app = applications.find((a) => a.id === ids[0]);
      if (app) {
        setError("");
        setJoiningTarget(app);
      }
      return;
    }

    // Interview stages need round type + date/time to send
    // InterviewScheduledMail -- the API rejects a plain bulk-move into
    // these (422, requires_schedule_interview). Open the real Schedule
    // Interview flow instead, pre-filled, one candidate at a time.
    if (["interview_round_1", "interview_round_2", "hr_round"].includes(stage)) {
      if (ids.length > 1) {
        setError("Select only one candidate at a time to schedule an interview.");
        return;
      }
      const app = applications.find((a) => a.id === ids[0]);
      if (app) {
        setError("");
        setScheduleTarget(app);
      }
      return;
    }

    // Moving to Offer needs salary + joining date to create a real Offer
    // record (and its PDF) -- a plain status flip leaves nothing behind
    // for the candidate to actually receive. Open Create Offer instead,
    // pre-filled, one candidate at a time.
    if (stage === "offer") {
      if (ids.length > 1) {
        setError("Select only one candidate at a time to create an offer.");
        return;
      }
      const app = applications.find((a) => a.id === ids[0]);
      if (app) {
        setError("");
        setOfferTarget(app);
      }
      return;
    }

    setBulkActionLoading(true);
    setError("");
    try {
      await api.post("/pipeline/bulk-move", { ids, status: stage });
      clearSelection();
      loadApplications();
    } catch {
      setError("Couldn't move the selected candidate(s).");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleCardStatusChange = async (appId, newStatus, currentStatus) => {
    if (!newStatus || newStatus === currentStatus) return;

    // Same as the board drop target: "Offer" needs salary + joining date to
    // create a real Offer record (and send the PDF/email), so route it
    // through Create Offer instead of a plain status PUT.
    if (newStatus === "offer") {
      const app = applications.find((a) => a.id === appId);
      if (app) {
        setError("");
        setOfferTarget(app);
      }
      return;
    }

    if (["interview_round_1", "interview_round_2", "hr_round"].includes(newStatus)) {
      const app = applications.find((a) => a.id === appId);
      if (app) {
        setError("");
        setScheduleTarget(app);
      }
      return;
    }

    try {
      await api.put(`/applications/${appId}/status`, { status: newStatus });
      loadApplications();
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't update status.");
    }
  };

  const handleCardDragStart = (id) => {
    if (!selected.has(id)) setSelected(new Set([id]));
  };

  const bulkReject = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0 || bulkActionLoading) return;
    setBulkActionLoading(true);
    setError("");
    try {
      await api.post("/pipeline/bulk-reject", { ids });
      clearSelection();
      loadApplications();
    } catch {
      setError("Couldn't reject the selected candidate(s).");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkShortlist = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0 || bulkActionLoading) return;
    setBulkActionLoading(true);
    setError("");
    try {
      await api.post("/pipeline/bulk-shortlist", { ids });
      clearSelection();
      loadApplications();
    } catch {
      setError("Couldn't shortlist the selected candidate(s).");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const runMatchScoring = async () => {
    if (!jobFilter || scoring) return;
    setScoring(true);
    setScoreError("");
    try {
      const res = await api.get("/pipeline/match-scores", {
        params: { job_posting_id: jobFilter },
      });
      const map = {};
      res.data.forEach((s) => {
        map[s.application_id] = s.score;
      });
      setMatchScores(map);
    } catch {
      setScoreError("Couldn't score candidates.");
    } finally {
      setScoring(false);
    }
  };

 const loadRecommendations = async () => {
  if (!jobFilter || loadingReco) return;
  setLoadingReco(true);
  setRecoError("");
  try {
    const [bestRes, hiddenRes, acceptRes, passiveRes] = await Promise.all([
      api.get("/recommendations/best-candidate", { params: { job_posting_id: jobFilter } }),
      api.get("/recommendations/hidden-talent", { params: { job_posting_id: jobFilter } }),
      api.get("/recommendations/likely-to-accept", { params: { job_posting_id: jobFilter } }),
      api.get("/recommendations/passive-candidates", { params: { job_posting_id: jobFilter } }),
    ]);
    setRecommendations({
      best: bestRes.data,
      hidden: hiddenRes.data,
      likely: acceptRes.data,
      passive: passiveRes.data,
    });
  } catch {
    setRecoError("Couldn't load recommendations.");
  } finally {
    setLoadingReco(false);
  }
};

  const loadSimilarCandidates = async (app) => {
    setSimilarTarget(app);
    setSimilarResults(null);
    setLoadingSimilar(true);
    try {
      const res = await api.get(`/recommendations/similar-candidates/${app.id}`);
      setSimilarResults(res.data);
    } catch {
      setSimilarResults([]);
    } finally {
      setLoadingSimilar(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8FF]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} activeTab="pipeline" />

       <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="px-4 py-8">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#0D1B3D] max-w-7xl mx-auto"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {showFiltersMobile && (
              <div className="max-w-7xl mx-auto">
                <EmployerSidebar user={user} activeTab="pipeline" mobile />
              </div>
            )}

        <div className="mx-auto mb-6 flex max-w-7xl items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${GRADIENT_TEXT}`}>
              Candidate Pipeline
            </h1>
            <p className="text-sm text-gray-500">Drag cards between stages, or select and bulk-move.</p>
          </div>
          <div className="text-right">
            <select
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All jobs</option>
              {jobPostings.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title}
                </option>
              ))}
            </select>
            {jobFilter && (
              <>
                <button
                  onClick={runMatchScoring}
                  disabled={scoring}
                  className={`ml-2 px-4 py-2 text-sm disabled:opacity-60 ${GRADIENT_BUTTON}`}
                >
                  {scoring ? "Scoring…" : "AI Match Scores"}
                </button>
                <button
                  onClick={loadRecommendations}
                  disabled={loadingReco}
                  className={`ml-2 px-4 py-2 text-sm disabled:opacity-60 ${GRADIENT_BUTTON}`}
                >
                  {loadingReco ? "Thinking…" : "AI Recommendations"}
                </button>
              </>
            )}
            {jobsError && <p className="mt-1 text-xs text-red-500">{jobsError}</p>}
            {scoreError && <p className="mt-1 text-xs text-red-500">{scoreError}</p>}
          </div>
        </div>

        {error && (
          <div className="mx-auto mb-4 flex max-w-7xl items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
            <span>{error}</span>
            <button
              onClick={loadApplications}
              className="ml-3 shrink-0 font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {recoError && (
          <div className="mx-auto mb-4 max-w-7xl rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
            {recoError}
          </div>
        )}

        {recommendations && (
          <div className="mx-auto mb-6 grid max-w-7xl grid-cols-1 gap-4 md:grid-cols-4">
            {/* Best Candidate */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: COLORS.dark }}>
                <FaStar style={{ color: "#F59E0B" }} /> Best Candidate
              </div>
              {recommendations.best ? (
                <>
                  <p className="text-sm font-medium" style={{ color: COLORS.dark }}>
                    {recommendations.best.candidate_name}
                  </p>
                  <p className="text-xs text-gray-500">{recommendations.best.score}% match</p>
                  {recommendations.best.reason && (
                    <p className="mt-1 text-xs text-gray-400">{recommendations.best.reason}</p>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-400">No applicants yet for this job.</p>
              )}
            </div>

            {/* Hidden Talent */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: COLORS.dark }}>
                <FaUserSecret style={{ color: COLORS.blue }} /> Hidden Talent
              </div>
              {recommendations.hidden.length === 0 ? (
                <p className="text-xs text-gray-400">No matches from your other job postings.</p>
              ) : (
                <ul className="space-y-1.5">
                  {recommendations.hidden.map((h) => (
                    <li key={h.application_id} className="text-xs">
                      <span className="font-medium" style={{ color: COLORS.dark }}>{h.candidate_name}</span>
                      <span className="text-gray-400"> · {h.score}% · applied to {h.currently_applied_to}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Likely to Accept */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: COLORS.dark }}>
                <FaHandshake style={{ color: "#16A34A" }} /> Likely to Accept
              </div>
              {recommendations.likely.length === 0 ? (
                <p className="text-xs text-gray-400">No applicants yet for this job.</p>
              ) : (
                <ul className="space-y-1.5">
                  {recommendations.likely.slice(0, 3).map((l) => (
                    <li key={l.application_id} className="text-xs">
                      <span className="font-medium" style={{ color: COLORS.dark }}>{l.candidate_name}</span>
                      <span className="text-gray-400"> · {l.score}%</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>


            {/* Passive Candidates */}
<div className="rounded-lg border border-gray-200 bg-white p-4">
  <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: COLORS.dark }}>
    <FaUserClock style={{ color: "#7C3AED" }} /> Passive Candidates
  </div>
  {recommendations.passive.length === 0 ? (
    <p className="text-xs text-gray-400">No passive candidates found.</p>
  ) : (
    <ul className="space-y-1.5">
      {recommendations.passive.map((p) => (
        <li key={p.user_id} className="text-xs">
          <span className="font-medium" style={{ color: COLORS.dark }}>{p.candidate_name}</span>
          <span className="text-gray-400"> · {p.score}% · {p.reason}</span>
        </li>
      ))}
    </ul>
  )}
</div>
          </div>
        )}

        {selected.size > 0 && (
          <div
            className="sticky top-2 z-10 mx-auto mb-4 flex max-w-7xl items-center justify-between rounded-2xl bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] px-4 py-3 text-white shadow-lg shadow-purple-500/25"
          >
            <span className="text-sm font-medium">{selected.size} selected</span>
            <div className="flex gap-2">
              <button
                onClick={bulkShortlist}
                disabled={bulkActionLoading}
                className="rounded-md bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30 disabled:opacity-50"
              >
                {bulkActionLoading ? "Working..." : "Shortlist"}
              </button>
              <button
                onClick={bulkReject}
                disabled={bulkActionLoading}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              >
                {bulkActionLoading ? "Working..." : "Reject"}
              </button>
              <button
                onClick={clearSelection}
                disabled={bulkActionLoading}
                className="rounded-md border border-white/30 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading pipeline...</div>
        ) : applications.length === 0 && !error ? (
          <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center text-gray-400">
            No applications {jobFilter ? "for this job yet" : "yet"}.
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {STAGES.map((stage) => (
              <div
                key={stage.key}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverStage(stage.key);
                }}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={() => handleDrop(stage.key)}
                className="flex w-64 flex-shrink-0 flex-col rounded-xl"
                style={{
                  backgroundColor: dragOverStage === stage.key ? "#F5F3FF" : "#FFFFFF",
                  border: dragOverStage === stage.key ? `2px dashed ${COLORS.blue}` : "1px solid #EDE9FE",
                }}
              >
                <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
                  <span className="text-sm font-semibold" style={{ color: COLORS.dark }}>
                    {stage.label}
                  </span>
                  <span className="rounded-full bg-gradient-to-r from-[#5B2EFF] to-[#FF4D8D] px-2 py-0.5 text-xs font-semibold text-white">
                    {columns[stage.key].length}
                  </span>
                </div>

                <div className="flex-1 space-y-2 p-2">
                  {columns[stage.key].length === 0 ? (
                    <div className="rounded-md border border-dashed border-gray-200 py-6 text-center text-xs text-gray-400">
                      No candidates
                    </div>
                  ) : (
                 columns[stage.key].map((app) => (
                      <div
                        key={app.id}
                        draggable
                        onDragStart={() => handleCardDragStart(app.id)}
                        onClick={() => setDetailTarget(app)}
                        className="cursor-pointer rounded-xl border bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-500/20"
                        style={{
                          borderColor: selected.has(app.id) ? COLORS.blue : "#E5E7EB",
                          boxShadow: selected.has(app.id) ? `0 0 0 2px ${COLORS.blue}` : undefined,
                        }}
                      >
                        <div className="mb-1 flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={selected.has(app.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleSelect(app.id)}
                            className="mt-0.5 shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate" style={{ color: COLORS.dark }}>
                              {app.candidate_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{app.job_title}</div>
                          </div>
                        </div>

                        <StatusDropdown
                          value={app.status}
                          label={labelForStatus(app.status)}
                          options={DIRECT_STATUS_OPTIONS}
                          onChange={(newStatus) => handleCardStatusChange(app.id, newStatus, app.status)}
                          bgColor="#F3F4F6"
                          textColor={COLORS.dark}
                        />

                        {matchScores[app.id] !== undefined && (
                          <div
                            className="mb-2 inline-block rounded-full px-2 py-0.5 text-xs font-semibold"
                            style={{ backgroundColor: "#ECFDF5", color: "#16A34A" }}
                          >
                            AI Match {matchScores[app.id]}%
                          </div>
                        )}

                        {(app.experience_years || app.expected_salary) && (
                          <div className="mb-2 flex gap-3 text-xs text-gray-500">
                            {app.experience_years && <span>{app.experience_years} yrs</span>}
                            {app.expected_salary && (
                              <span>₹{Number(app.expected_salary).toLocaleString("en-IN")}</span>
                            )}
                          </div>
                        )}

                        {app.tags && app.tags.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-1">
                            {app.tags.map((t) => (
                              <span
                                key={t}
                                className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                                style={{ backgroundColor: "#FFFBEB", color: COLORS.orange }}
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}

                       <div className="flex flex-wrap items-center gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setNotesTarget(app);
                            }}
                            className="flex items-center gap-1 text-xs font-medium"
                            style={{ color: COLORS.blue }}
                          >
                            {app.notes ? <FaStickyNote /> : <FaTag />}
                            {app.notes ? "Edit notes" : "Add notes/tags"}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              loadSimilarCandidates(app);
                            }}
                            className="flex items-center gap-1 text-xs font-medium"
                            style={{ color: "#7C3AED" }}
                          >
                            <FaUsers /> Similar
                          </button>
                          {app.resume_path && (
                            <a
                              href={app.resume_path.startsWith('http') ? app.resume_path : `${BACKEND_URL}/storage/${app.resume_path}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 text-xs font-medium"
                              style={{ color: "#16A34A" }}
                            >
                              📄 Resume
                            </a>
                          )}
                          {stage.key === "offer" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setError("");
                                setJoiningTarget(app);
                              }}
                              className="flex items-center gap-1 text-xs font-medium"
                              style={{ color: "#16A34A" }}
                            >
                              Mark joined
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {notesTarget && (
          <CandidateNotesModal
            application={notesTarget}
            onClose={() => setNotesTarget(null)}
            onSaved={loadApplications}
          />
        )}

{joiningTarget && (
          <JoiningConfirmModal
            application={joiningTarget}
            onClose={() => setJoiningTarget(null)}
            onConfirmed={() => {
              setJoiningTarget(null);
              clearSelection();
              loadApplications();
            }}
          />
        )}

        {detailTarget && (
          <ApplicantDetailModal
            application={detailTarget}
            onClose={() => setDetailTarget(null)}
            onUpdate={loadApplications}
            onOffer={(app) => {
              setDetailTarget(null);
              setOfferTarget(app);
            }}
            onSchedule={(app) => {
              setDetailTarget(null);
              setScheduleTarget(app);
            }}
          />
        )}

        {scheduleTarget && (
          <ScheduleInterviewModal
            jobPostings={jobPostings}
            initialJobPostingId={scheduleTarget.job_posting_id}
            initialJobApplicationId={scheduleTarget.id}
            initialCandidateId={scheduleTarget.candidate_id}
            onClose={() => setScheduleTarget(null)}
            onScheduled={() => {
              setScheduleTarget(null);
              clearSelection();
              loadApplications();
            }}
          />
        )}

        {offerTarget && (
          <CreateOfferModal
            jobPostings={jobPostings}
            initialJobPostingId={offerTarget.job_posting_id}
            initialJobApplicationId={offerTarget.id}
            initialCandidateId={offerTarget.candidate_id}
            initialCandidateName={offerTarget.candidate_name}
            onClose={() => setOfferTarget(null)}
            onCreated={() => {
              setOfferTarget(null);
              clearSelection();
              loadApplications();
            }}
          />
        )}

        {similarTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
              <div className="flex items-center justify-between rounded-t-xl px-6 py-4" style={{ backgroundColor: COLORS.dark }}>
                <h2 className="text-sm font-semibold text-white">
                  Similar to {similarTarget.candidate_name}
                </h2>
                <button onClick={() => setSimilarTarget(null)} className="text-white/70 hover:text-white">
                  ×
                </button>
              </div>
              <div className="p-5">
                {loadingSimilar ? (
                  <p className="text-sm text-gray-400">Finding similar candidates...</p>
                ) : similarResults && similarResults.length > 0 ? (
                  <ul className="space-y-2">
                    {similarResults.map((s) => (
                      <li key={s.application_id} className="flex items-center justify-between text-sm">
                        <span style={{ color: COLORS.dark }}>{s.candidate_name}</span>
                        <span className="text-xs text-gray-400">{s.score}%</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400">No similar candidates found.</p>
                )}
              </div>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}